import React, { useEffect, useState } from "react";
import PortalShell from "@/components/PortalShell";
import { api, formatApiError } from "@/lib/api";
import { toast, Toaster } from "sonner";
import { Clock, Users, AlertCircle, CheckCircle2, Download, Lock, Unlock, MessageSquare, Filter, History, Edit3, Plus, X } from "lucide-react";

const flagLabels = {
  long_shift: "Long shift",
  missing_clockout: "Missed clock-out",
  missing_location: "No location",
  unassigned_client: "Unassigned client",
};

function StatCard({ label, value, icon: Icon, tone = "navy", testId }) {
  const tones = {
    navy: "bg-indigo-50 text-brand-navy",
    orange: "bg-orange-50 text-brand-orange",
    rose: "bg-rose-50 text-rose-700",
    emerald: "bg-emerald-50 text-emerald-700",
  };
  return (
    <div className="wc-card p-5" data-testid={testId}>
      <div className={`w-10 h-10 rounded-xl grid place-items-center ${tones[tone]}`}><Icon className="w-4 h-4"/></div>
      <div className="mt-3 font-outfit text-3xl font-semibold text-brand-navy">{value}</div>
      <div className="text-xs text-slate-500 mt-1">{label}</div>
    </div>
  );
}

function FlagPills({ flags }) {
  if (!flags || flags.length === 0) return null;
  return (
    <div className="flex flex-wrap gap-1">
      {flags.map((f) => (
        <span key={f} className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-rose-50 text-rose-700 font-semibold uppercase tracking-wider">
          <AlertCircle className="w-2.5 h-2.5"/> {flagLabels[f] || f}
        </span>
      ))}
    </div>
  );
}

function toLocalInput(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const off = d.getTimezoneOffset();
  return new Date(d.getTime() - off * 60000).toISOString().slice(0, 16);
}

function EditPunchModal({ punch, clients, services, onClose, onSaved }) {
  const [form, setForm] = useState({
    client_id: punch.client_id || "",
    service_type_id: punch.service_type_id || "",
    clock_in: toLocalInput(punch.clock_in),
    clock_out: toLocalInput(punch.clock_out),
    reason: "",
  });
  const [history, setHistory] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/clock/admin/punch/${punch.id}/history`).then(({ data }) => setHistory(data)).catch(() => {});
  }, [punch.id]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.reason.trim()) { toast.error("Reason is required"); return; }
    setBusy(true);
    try {
      await api.patch(`/clock/admin/punch/${punch.id}`, {
        client_id: form.client_id || null,
        service_type_id: form.service_type_id || null,
        clock_in: form.clock_in ? new Date(form.clock_in).toISOString() : null,
        clock_out: form.clock_out ? new Date(form.clock_out).toISOString() : null,
        reason: form.reason,
      });
      toast.success("Punch updated. Audit trail recorded.");
      onSaved();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-6 overflow-auto" data-testid="edit-punch-modal">
      <form onSubmit={submit} className="w-full max-w-lg bg-white rounded-3xl p-6 my-6">
        <div className="flex items-center justify-between">
          <h3 className="font-outfit text-xl font-semibold text-brand-navy">Correct punch</h3>
          <button type="button" onClick={onClose} className="text-slate-400 hover:text-slate-600"><X className="w-5 h-5"/></button>
        </div>
        <div className="text-xs text-slate-500 mt-1">Employee: <span className="text-brand-navy font-semibold">{punch.employee_name}</span></div>

        <div className="mt-4 grid grid-cols-1 gap-3">
          <div>
            <label className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Client</label>
            <select value={form.client_id} onChange={(e)=>setForm({...form, client_id: e.target.value})} data-testid="edit-client"
              className="mt-1.5 w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm bg-white">
              <option value="">— unassigned —</option>
              {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Service type</label>
            <select value={form.service_type_id} onChange={(e)=>setForm({...form, service_type_id: e.target.value})} data-testid="edit-service"
              className="mt-1.5 w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm bg-white">
              {services.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Clock in</label>
              <input type="datetime-local" value={form.clock_in} onChange={(e)=>setForm({...form, clock_in: e.target.value})} data-testid="edit-in"
                className="mt-1.5 w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm"/>
            </div>
            <div>
              <label className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Clock out</label>
              <input type="datetime-local" value={form.clock_out} onChange={(e)=>setForm({...form, clock_out: e.target.value})} data-testid="edit-out"
                className="mt-1.5 w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm"/>
            </div>
          </div>
          <div>
            <label className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Reason (required)</label>
            <textarea required rows={2} value={form.reason} onChange={(e)=>setForm({...form, reason: e.target.value})} data-testid="edit-reason"
              className="mt-1.5 w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm"/>
          </div>
        </div>

        <div className="mt-5 flex gap-3 justify-end">
          <button type="button" onClick={onClose} className="wc-btn-outline">Cancel</button>
          <button type="submit" disabled={busy} className="wc-btn-primary disabled:opacity-60" data-testid="edit-save">
            {busy ? "Saving…" : "Save with audit"}
          </button>
        </div>

        {/* Audit history */}
        {history.length > 0 && (
          <div className="mt-6 pt-5 border-t border-slate-100">
            <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500 inline-flex items-center gap-1"><History className="w-3 h-3"/> Audit history</div>
            <div className="mt-3 space-y-3 max-h-52 overflow-auto">
              {history.map((h) => (
                <div key={h.id} className="text-xs bg-slate-50 rounded-xl p-3">
                  <div className="font-semibold text-brand-navy">{h.action.toUpperCase()} · {h.admin_name}</div>
                  <div className="text-slate-500 mt-0.5">{new Date(h.at).toLocaleString()}</div>
                  <div className="mt-1 text-slate-700">Reason: {h.reason}</div>
                </div>
              ))}
            </div>
          </div>
        )}
      </form>
    </div>
  );
}

export default function AdminClock() {
  const [dash, setDash] = useState(null);
  const [tab, setTab] = useState("overview");
  const [filters, setFilters] = useState({ employee_id: "", client_id: "", service_type_id: "", start: "", end: "" });
  const [timecards, setTimecards] = useState(null);
  const [clients, setClients] = useState([]);
  const [services, setServices] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [pay, setPay] = useState([]);
  const [editing, setEditing] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [addForm, setAddForm] = useState({ employee_id: "", client_id: "", service_type_id: "", clock_in: "", clock_out: "", reason: "" });

  const load = async () => {
    try {
      const [d, cs, ss, us, ps] = await Promise.all([
        api.get("/clock/admin/dashboard"),
        api.get("/clock/admin/clients"),
        api.get("/clock/admin/service-types"),
        api.get("/admin/users?role=employee"),
        api.get("/clock/admin/pay-periods"),
      ]);
      setDash(d.data); setClients(cs.data); setServices(ss.data); setEmployees(us.data); setPay(ps.data);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const loadTimecards = async () => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => { if (v) params.append(k, v); });
    try {
      const { data } = await api.get(`/clock/admin/timecards?${params.toString()}`);
      setTimecards(data);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const approveReq = async (id) => {
    try { await api.post(`/clock/admin/requests/${id}/approve`); toast.success("Approved"); await load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const rejectReq = async (id) => {
    const reason = window.prompt("Reason for rejection?");
    if (!reason) return;
    try { await api.post(`/clock/admin/requests/${id}/reject`, { reason }); toast.success("Rejected"); await load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const lockPeriod = async (id) => {
    if (!window.confirm("Lock this pay period? Punches inside cannot be edited without a reopen.")) return;
    try { await api.post(`/clock/admin/pay-periods/${id}/lock`); toast.success("Period locked"); await load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const reopenPeriod = async (id) => {
    const reason = window.prompt("Reason to reopen?");
    if (!reason) return;
    try { await api.post(`/clock/admin/pay-periods/${id}/reopen`, { reason }); toast.success("Reopened"); await load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const exportCSV = async (id) => {
    try {
      const res = await api.get(`/clock/admin/pay-periods/${id}/export.csv`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url; a.download = `wecareii_payroll_${id}.csv`; document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const submitAdd = async (e) => {
    e.preventDefault();
    if (!addForm.reason.trim()) { toast.error("Reason required"); return; }
    try {
      await api.post("/clock/admin/punch", {
        ...addForm,
        client_id: addForm.client_id || null,
        clock_in: new Date(addForm.clock_in).toISOString(),
        clock_out: addForm.clock_out ? new Date(addForm.clock_out).toISOString() : null,
      });
      toast.success("Shift added (audit recorded)");
      setShowAdd(false); setAddForm({ employee_id: "", client_id: "", service_type_id: "", clock_in: "", clock_out: "", reason: "" });
      await load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  if (!dash) return <PortalShell variant="admin"><div className="text-slate-500 text-sm">Loading…</div></PortalShell>;

  return (
    <PortalShell variant="admin">
      <Toaster richColors position="top-right" />

      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
        <div>
          <span className="wc-eyebrow-navy">Time Clock</span>
          <h1 className="mt-3 font-outfit text-4xl md:text-5xl font-semibold tracking-tight text-brand-navy">Payroll Operations</h1>
          <p className="mt-3 text-slate-600 text-sm max-w-lg">Manage punches, correction requests, pay periods, clients, and service rates. All corrections are audit-logged.</p>
        </div>
        <button onClick={() => setShowAdd(true)} className="wc-btn-primary" data-testid="admin-add-shift-btn"><Plus className="w-4 h-4"/> Add shift</button>
      </div>

      {/* Tabs */}
      <div className="mt-8 flex gap-1 flex-wrap border-b border-slate-100">
        {[
          { id: "overview", label: "Overview" },
          { id: "timecards", label: "Timecards" },
          { id: "requests", label: `Requests (${dash.kpi.pending_requests})` },
          { id: "periods", label: "Pay Periods" },
          { id: "config", label: "Clients & Rates" },
        ].map((t) => (
          <button key={t.id} onClick={() => { setTab(t.id); if (t.id === "timecards" && !timecards) loadTimecards(); }} data-testid={`tab-${t.id}`}
            className={`px-4 py-3 text-sm font-semibold border-b-2 ${tab === t.id ? "border-brand-orange text-brand-navy" : "border-transparent text-slate-500 hover:text-brand-navy"}`}
            style={{ transition: "color 200ms ease, border-color 200ms ease" }}>
            {t.label}
          </button>
        ))}
      </div>

      {/* Overview */}
      {tab === "overview" && (
        <>
          <div className="mt-8 grid grid-cols-2 md:grid-cols-5 gap-4">
            <StatCard label="Clocked in now" value={dash.kpi.clocked_in_now} icon={Users} tone="emerald" testId="kpi-clocked-in"/>
            <StatCard label="Today's punches" value={dash.kpi.today_punches} icon={Clock} tone="navy" testId="kpi-today"/>
            <StatCard label="Missed clock-outs" value={dash.kpi.missed_clockouts} icon={AlertCircle} tone="rose" testId="kpi-missed"/>
            <StatCard label="Pending requests" value={dash.kpi.pending_requests} icon={MessageSquare} tone="orange" testId="kpi-requests"/>
            <StatCard label="Overtime flagged" value={dash.kpi.ot_flagged} icon={AlertCircle} tone="orange" testId="kpi-ot"/>
          </div>

          <div className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="wc-card p-6" data-testid="clocked-in-list">
              <h3 className="font-outfit text-lg font-semibold text-brand-navy">Clocked in right now</h3>
              <div className="mt-4 divide-y divide-slate-100">
                {dash.clocked_in_now.length === 0 && <div className="text-sm text-slate-500 py-4">No one is clocked in.</div>}
                {dash.clocked_in_now.map((p) => (
                  <div key={p.id} className="py-3 flex items-center gap-3">
                    <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"/>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-brand-navy truncate">{p.employee_name}</div>
                      <div className="text-xs text-slate-500">{p.client_name || "—"} · {p.service_type_name || ""}</div>
                    </div>
                    <div className="text-xs text-slate-500">since {new Date(p.clock_in).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}</div>
                    <button onClick={() => setEditing(p)} className="text-brand-orange hover:brightness-90 text-xs font-semibold inline-flex items-center gap-1"><Edit3 className="w-3 h-3"/> Edit</button>
                  </div>
                ))}
              </div>
            </div>

            <div className="wc-card p-6" data-testid="ot-list">
              <h3 className="font-outfit text-lg font-semibold text-brand-navy">Overtime this week</h3>
              <div className="mt-4 space-y-3">
                {dash.overtime_approaching.length === 0 && <div className="text-sm text-slate-500">No one over 32 hours yet.</div>}
                {dash.overtime_approaching.map((o) => (
                  <div key={o.employee_id} className="flex items-center justify-between">
                    <div className="text-sm font-medium text-brand-navy">{o.employee_name}</div>
                    <div className={`text-sm font-semibold tabular-nums ${o.over ? "text-rose-600" : "text-brand-orange"}`}>{o.hours}h {o.over ? "· OVER 40" : ""}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="mt-6 wc-card p-6" data-testid="today-punches">
            <h3 className="font-outfit text-lg font-semibold text-brand-navy">Today&apos;s punches</h3>
            <div className="mt-4 overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-xs uppercase tracking-wider text-slate-500 font-semibold">
                  <tr className="border-b border-slate-100"><th className="py-2 text-left">Employee</th><th className="text-left">Client</th><th className="text-left">Service</th><th className="text-left">In</th><th className="text-left">Out</th><th className="text-right">Hours</th><th></th></tr>
                </thead>
                <tbody>
                  {dash.today_punches.map((p) => (
                    <tr key={p.id} className="border-b border-slate-100" data-testid={`today-row-${p.id}`}>
                      <td className="py-2 font-medium text-brand-navy">{p.employee_name}</td>
                      <td className="text-slate-600">{p.client_name || "—"}</td>
                      <td className="text-slate-600">{p.service_type_name || "—"}</td>
                      <td className="text-slate-600">{new Date(p.clock_in).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}</td>
                      <td className="text-slate-600">{p.clock_out ? new Date(p.clock_out).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" }) : <span className="text-brand-orange font-semibold">Open</span>}</td>
                      <td className="text-right font-semibold text-brand-navy tabular-nums">{p.hours.toFixed(2)}</td>
                      <td className="text-right pl-2"><button onClick={() => setEditing(p)} className="text-brand-orange hover:brightness-90 text-xs font-semibold inline-flex items-center gap-1" data-testid={`edit-punch-${p.id}`}><Edit3 className="w-3 h-3"/> Edit</button></td>
                    </tr>
                  ))}
                  {dash.today_punches.length === 0 && <tr><td colSpan={7} className="py-6 text-center text-slate-500 text-sm">No punches yet today.</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {/* Timecards */}
      {tab === "timecards" && (
        <div className="mt-8">
          <div className="wc-card p-5 flex flex-wrap gap-3 items-end" data-testid="timecards-filters">
            <div>
              <label className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Employee</label>
              <select value={filters.employee_id} onChange={(e)=>setFilters({...filters, employee_id: e.target.value})} data-testid="filter-employee" className="mt-1.5 rounded-xl border border-slate-200 px-3 py-2 text-sm bg-white">
                <option value="">All</option>
                {employees.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Client</label>
              <select value={filters.client_id} onChange={(e)=>setFilters({...filters, client_id: e.target.value})} data-testid="filter-client" className="mt-1.5 rounded-xl border border-slate-200 px-3 py-2 text-sm bg-white">
                <option value="">All</option>
                {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Service</label>
              <select value={filters.service_type_id} onChange={(e)=>setFilters({...filters, service_type_id: e.target.value})} data-testid="filter-service" className="mt-1.5 rounded-xl border border-slate-200 px-3 py-2 text-sm bg-white">
                <option value="">All</option>
                {services.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Start</label>
              <input type="date" value={filters.start} onChange={(e)=>setFilters({...filters, start: e.target.value})} data-testid="filter-start" className="mt-1.5 rounded-xl border border-slate-200 px-3 py-2 text-sm"/>
            </div>
            <div>
              <label className="text-[11px] font-bold uppercase tracking-wider text-slate-500">End</label>
              <input type="date" value={filters.end} onChange={(e)=>setFilters({...filters, end: e.target.value})} data-testid="filter-end" className="mt-1.5 rounded-xl border border-slate-200 px-3 py-2 text-sm"/>
            </div>
            <button onClick={loadTimecards} className="wc-btn-primary" data-testid="apply-filters-btn"><Filter className="w-4 h-4"/> Apply</button>
          </div>

          {timecards && (
            <div className="mt-6 wc-card p-6" data-testid="timecards-table">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="text-xs uppercase tracking-wider text-slate-500 font-semibold">
                    <tr className="border-b border-slate-100"><th className="py-2 text-left">Employee</th><th className="text-left">Client</th><th className="text-left">Service</th><th className="text-left">In</th><th className="text-left">Out</th><th className="text-right">Hours</th><th className="text-left">Flags</th><th></th></tr>
                  </thead>
                  <tbody>
                    {timecards.rows.map((p) => (
                      <tr key={p.id} className="border-b border-slate-100">
                        <td className="py-2 font-medium text-brand-navy">{p.employee_name}</td>
                        <td className="text-slate-600">{p.client_name || "—"}</td>
                        <td className="text-slate-600">{p.service_type_name || "—"}</td>
                        <td className="text-slate-600 text-xs">{new Date(p.clock_in).toLocaleString()}</td>
                        <td className="text-slate-600 text-xs">{p.clock_out ? new Date(p.clock_out).toLocaleString() : <span className="text-brand-orange font-semibold">Open</span>}</td>
                        <td className="text-right font-semibold text-brand-navy tabular-nums">{p.hours.toFixed(2)}</td>
                        <td><FlagPills flags={p.flags}/></td>
                        <td className="text-right pl-2"><button onClick={() => setEditing(p)} className="text-brand-orange hover:brightness-90 text-xs font-semibold inline-flex items-center gap-1"><Edit3 className="w-3 h-3"/> Edit</button></td>
                      </tr>
                    ))}
                    {timecards.rows.length === 0 && <tr><td colSpan={8} className="py-6 text-center text-slate-500 text-sm">No records match filters.</td></tr>}
                  </tbody>
                </table>
              </div>
              {timecards.totals_by_workweek.length > 0 && (
                <div className="mt-6 pt-4 border-t border-slate-100">
                  <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Workweek totals</div>
                  <div className="mt-2 grid grid-cols-1 md:grid-cols-3 gap-2 text-sm">
                    {timecards.totals_by_workweek.map((t) => (
                      <div key={t.employee_id + t.workweek} className="rounded-xl bg-slate-50 px-3 py-2">
                        <span className="text-slate-500">{t.workweek}</span> · <span className="font-semibold text-brand-navy">{t.hours}h</span> {t.overtime_hours > 0 && <span className="text-rose-600 font-semibold">({t.overtime_hours}h OT)</span>}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Requests */}
      {tab === "requests" && (
        <div className="mt-8 wc-card p-6" data-testid="requests-table">
          <h3 className="font-outfit text-lg font-semibold text-brand-navy">Correction requests</h3>
          <div className="mt-4 divide-y divide-slate-100">
            {dash.pending_requests.length === 0 && <div className="text-sm text-slate-500 py-4">No pending requests.</div>}
            {dash.pending_requests.map((r) => (
              <div key={r.id} className="py-4" data-testid={`request-row-${r.id}`}>
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <div className="text-sm font-medium text-brand-navy">{r.employee_name} · <span className="text-slate-500 text-xs uppercase tracking-wider">{r.punch_id ? "punch correction" : "missing shift"}</span></div>
                    <div className="text-xs text-slate-500 mt-0.5">{new Date(r.created_at).toLocaleString()}</div>
                    <div className="mt-2 text-sm text-slate-700">{r.explanation}</div>
                    {(r.requested_clock_in || r.requested_clock_out) && (
                      <div className="mt-1 text-xs text-slate-500">
                        Requested: {r.requested_clock_in ? new Date(r.requested_clock_in).toLocaleString() : "—"} → {r.requested_clock_out ? new Date(r.requested_clock_out).toLocaleString() : "—"}
                      </div>
                    )}
                  </div>
                  <div className="flex gap-2 shrink-0">
                    <button onClick={() => approveReq(r.id)} className="wc-btn-primary !py-2 !px-3 text-xs" data-testid={`approve-${r.id}`}><CheckCircle2 className="w-3.5 h-3.5"/> Approve</button>
                    <button onClick={() => rejectReq(r.id)} className="wc-btn-outline !py-2 !px-3 text-xs" data-testid={`reject-${r.id}`}>Reject</button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Pay periods */}
      {tab === "periods" && (
        <div className="mt-8 wc-card p-6" data-testid="periods-table">
          <h3 className="font-outfit text-lg font-semibold text-brand-navy">Pay periods</h3>
          <div className="mt-4 overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-xs uppercase tracking-wider text-slate-500 font-semibold">
                <tr className="border-b border-slate-100"><th className="py-2 text-left">Label</th><th className="text-left">Range</th><th className="text-left">Status</th><th className="text-right">Actions</th></tr>
              </thead>
              <tbody>
                {pay.map((p) => (
                  <tr key={p.id} className="border-b border-slate-100" data-testid={`period-row-${p.id}`}>
                    <td className="py-2 font-medium text-brand-navy">{p.label}</td>
                    <td className="text-slate-600">{p.start_date} → {p.end_date}</td>
                    <td>
                      <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium ${p.locked ? "bg-slate-100 text-slate-700" : "bg-emerald-50 text-emerald-700"}`}>
                        {p.locked ? <><Lock className="w-3 h-3"/> Locked</> : <><Unlock className="w-3 h-3"/> Open</>}
                      </span>
                    </td>
                    <td className="text-right space-x-2">
                      <button onClick={() => exportCSV(p.id)} className="text-brand-orange hover:brightness-90 text-xs font-semibold inline-flex items-center gap-1" data-testid={`export-${p.id}`}><Download className="w-3.5 h-3.5"/> Export CSV</button>
                      {p.locked
                        ? <button onClick={() => reopenPeriod(p.id)} className="text-brand-navy hover:brightness-90 text-xs font-semibold" data-testid={`reopen-${p.id}`}>Reopen</button>
                        : <button onClick={() => lockPeriod(p.id)} className="text-brand-navy hover:brightness-90 text-xs font-semibold" data-testid={`lock-${p.id}`}>Lock</button>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Config: clients + service types */}
      {tab === "config" && (
        <div className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="wc-card p-6" data-testid="service-types-card">
            <h3 className="font-outfit text-lg font-semibold text-brand-navy">Service types & rates</h3>
            <div className="mt-4 space-y-3">
              {services.map((s) => (
                <div key={s.id} className="flex items-center gap-3">
                  <div className="flex-1 text-sm font-medium text-brand-navy">{s.name}</div>
                  <div className="text-xs text-slate-500">$</div>
                  <input type="number" step="0.01" defaultValue={s.default_rate} onBlur={async (e) => {
                    const v = parseFloat(e.target.value);
                    if (isNaN(v) || v === s.default_rate) return;
                    try { await api.patch(`/clock/admin/service-types/${s.id}`, { default_rate: v }); toast.success("Rate updated"); await load(); }
                    catch (err) { toast.error(formatApiError(err)); }
                  }} data-testid={`rate-${s.id}`} className="w-24 rounded-xl border border-slate-200 px-3 py-2 text-sm"/>
                  <div className="text-xs text-slate-500">/hr</div>
                </div>
              ))}
            </div>
          </div>

          <div className="wc-card p-6" data-testid="clients-card">
            <h3 className="font-outfit text-lg font-semibold text-brand-navy">Clients & assignments</h3>
            <div className="mt-4 space-y-3">
              {clients.map((c) => (
                <div key={c.id} className="rounded-xl bg-slate-50 p-3">
                  <div className="flex items-center justify-between">
                    <div className="text-sm font-medium text-brand-navy">{c.name}<span className="text-slate-400 text-xs ml-2">{c.reference}</span></div>
                    <div className="text-xs text-slate-500">{c.assigned_employee_ids.length} assigned</div>
                  </div>
                  <div className="mt-2 flex flex-wrap gap-1">
                    {employees.map((e) => {
                      const assigned = c.assigned_employee_ids.includes(e.id);
                      return (
                        <button key={e.id} onClick={async () => {
                          try {
                            if (assigned) await api.delete(`/clock/admin/clients/${c.id}/assign/${e.id}`);
                            else await api.post(`/clock/admin/clients/${c.id}/assign`, { employee_id: e.id });
                            await load();
                          } catch (err) { toast.error(formatApiError(err)); }
                        }} data-testid={`assign-${c.id}-${e.id}`}
                          className={`text-xs px-2.5 py-1 rounded-full font-medium ${assigned ? "bg-brand-navy text-white" : "bg-white text-slate-600 border border-slate-200"}`}
                          style={{ transition: "background-color 200ms ease, color 200ms ease" }}>
                          {e.name}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {editing && (
        <EditPunchModal punch={editing} clients={clients} services={services} onClose={() => setEditing(null)} onSaved={async () => { setEditing(null); await load(); if (tab === "timecards") await loadTimecards(); }}/>
      )}

      {showAdd && (
        <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-6" data-testid="add-shift-modal">
          <form onSubmit={submitAdd} className="w-full max-w-md bg-white rounded-3xl p-6">
            <div className="flex items-center justify-between">
              <h3 className="font-outfit text-xl font-semibold text-brand-navy">Add missing shift</h3>
              <button type="button" onClick={() => setShowAdd(false)} className="text-slate-400 hover:text-slate-600"><X className="w-5 h-5"/></button>
            </div>

            <div className="mt-4 grid grid-cols-1 gap-3">
              <div>
                <label className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Employee</label>
                <select required value={addForm.employee_id} onChange={(e)=>setAddForm({...addForm, employee_id: e.target.value})} data-testid="add-employee" className="mt-1.5 w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm bg-white">
                  <option value="">Select…</option>
                  {employees.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
                </select>
              </div>
              <div>
                <label className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Client</label>
                <select value={addForm.client_id} onChange={(e)=>setAddForm({...addForm, client_id: e.target.value})} data-testid="add-client" className="mt-1.5 w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm bg-white">
                  <option value="">— none —</option>
                  {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
              <div>
                <label className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Service type</label>
                <select required value={addForm.service_type_id} onChange={(e)=>setAddForm({...addForm, service_type_id: e.target.value})} data-testid="add-service" className="mt-1.5 w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm bg-white">
                  <option value="">Select…</option>
                  {services.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Clock in</label>
                  <input required type="datetime-local" value={addForm.clock_in} onChange={(e)=>setAddForm({...addForm, clock_in: e.target.value})} data-testid="add-in" className="mt-1.5 w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm"/>
                </div>
                <div>
                  <label className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Clock out</label>
                  <input type="datetime-local" value={addForm.clock_out} onChange={(e)=>setAddForm({...addForm, clock_out: e.target.value})} data-testid="add-out" className="mt-1.5 w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm"/>
                </div>
              </div>
              <div>
                <label className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Reason (required)</label>
                <textarea required rows={2} value={addForm.reason} onChange={(e)=>setAddForm({...addForm, reason: e.target.value})} data-testid="add-reason" className="mt-1.5 w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm"/>
              </div>
            </div>

            <div className="mt-5 flex gap-3 justify-end">
              <button type="button" onClick={() => setShowAdd(false)} className="wc-btn-outline">Cancel</button>
              <button type="submit" className="wc-btn-primary" data-testid="add-shift-save">Add shift</button>
            </div>
          </form>
        </div>
      )}
    </PortalShell>
  );
}
